/**
 * SN-7849 — when web Home's pending-invitations popup may open, and the record
 * that keeps it to once per sign-in.
 *
 * The record lives in localStorage as `{ userId }`: a reload keeps it, App
 * clears it on a manual sign-in (never on a session restore), and another
 * account's record does not count. The page keeps its own copy as well, so
 * storage that refuses the write (full, or locked down) cannot reopen the popup
 * the moment it closes; without storage, a reload shows it again.
 */

export const PENDING_INVITES_POPUP_KEY = 'sn:pending-invites-popup'

/**
 * The accounts the popup has shown to since this page loaded; App's sign-in
 * clears it along with the stored record.
 */
const shownSincePageLoad = new Set<string>()

const browserStorage = (): Storage | null => {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

const storedUserId = (storage: Storage | null): string | null => {
  try {
    const record = storage?.getItem(PENDING_INVITES_POPUP_KEY)
    return record ? JSON.parse(record)?.userId ?? null : null
  } catch {
    return null
  }
}

const removeStoredRecord = (storage: Storage | null): void => {
  try {
    storage?.removeItem(PENDING_INVITES_POPUP_KEY)
  } catch {
    // Unwritable storage: nothing was recorded there either.
  }
}

export const hasShownPendingInvitesPopup = (
  userId: string | null | undefined,
  storage: Storage | null = browserStorage(),
  page: Set<string> = shownSincePageLoad
): boolean => {
  if (!userId) {
    return false
  }
  return page.has(userId) || storedUserId(storage) === userId
}

export const markPendingInvitesPopupShown = (
  userId: string,
  storage: Storage | null = browserStorage(),
  page: Set<string> = shownSincePageLoad
): void => {
  page.add(userId)
  try {
    storage?.setItem(PENDING_INVITES_POPUP_KEY, JSON.stringify({ userId }))
  } catch {
    // Unwritable storage: the page's copy holds until a reload.
  }
}

/** Takes back a showing another modal cut short, so the popup can come back once it closes. */
export const forgetPendingInvitesPopupShown = (
  userId: string | null | undefined,
  storage: Storage | null = browserStorage(),
  page: Set<string> = shownSincePageLoad
): void => {
  if (!userId) {
    return
  }
  page.delete(userId)
  if (storedUserId(storage) === userId) {
    removeStoredRecord(storage)
  }
}

/** A new sign-in: the popup may show again. */
export const clearPendingInvitesPopupRecord = (
  storage: Storage | null = browserStorage(),
  page: Set<string> = shownSincePageLoad
): void => {
  page.clear()
  removeStoredRecord(storage)
}

export interface PendingInvitesPopupConditions {
  userId: string | null | undefined
  /** Web is framed inside another site (the Canlan widget): no Home popups there. */
  inFrame: boolean
  /** At least one pending invitation, in data fetched since Home mounted. */
  hasFreshPendingInvitations: boolean
  /** Onboarding has had its say; it goes first when it runs. */
  onboardingSettled: boolean
  /** Another Home modal is open (onboarding, the post composer, the timezone check). */
  otherModalOpen: boolean
  /** Already shown this sign-in. */
  alreadyShown: boolean
}

export const shouldOpenPendingInvitesPopup = ({
  userId,
  inFrame,
  hasFreshPendingInvitations,
  onboardingSettled,
  otherModalOpen,
  alreadyShown,
}: PendingInvitesPopupConditions): boolean =>
  Boolean(userId) &&
  !inFrame &&
  hasFreshPendingInvitations &&
  onboardingSettled &&
  !otherModalOpen &&
  !alreadyShown
